import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import '../../../App.css';
import { userService } from '../../../api/apiService';
import 'moment/locale/de'; // Import the German locale
import { websocketService } from '../../../services/websocketService';
import TalentCalendarApi from './TalentCalendarApi';
import { TalentCalendarProvider } from '../../../contexts/TalentCalendarContext';

// Set locale to German
moment.locale("de");

const MyBookingsPage = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [talentIds, setTalentIds] = useState([]);
    const [userEmail, setUserEmail] = useState(null);
    const [view, setView] = useState('calendar');

    useEffect(() => {
        console.log("MyBookingsPage component mounted");
        // Connect to WebSocket
        websocketService.connect();

        // Handle booking updates
        const handleBookingUpdate = (data) => {
            console.log('Received booking update:', data);
            // The TalentCalendarApi component will handle its own data refresh
        };

        // Subscribe to booking updates
        websocketService.subscribe('booking', handleBookingUpdate);

        // Set userEmail from localStorage and fetch user profile
        const email = localStorage.getItem("userEmail");
        console.log("User email from localStorage:", email);
        setUserEmail(email);
        
        if (email) {
            fetchUserProfile();
        } else {
            console.error("No userEmail found in localStorage");
            setError("User email not found. Please login again.");
            setIsLoading(false);
        }

        // Cleanup on unmount
        return () => {
            console.log("MyBookingsPage component unmounted");
            websocketService.unsubscribe('booking', handleBookingUpdate);
            websocketService.disconnect();
        };
    }, []);

    const fetchUserProfile = async () => {
        console.log("Fetching user profile");
        try {
            setIsLoading(true);
            const response = await userService.getProfile();
            console.log("User profile response:", response);
            
            if (response && response.data && response.data.user) {
                const userData = response.data.user;
                
                // Check if talents array exists and has items
                if (userData.talents && Array.isArray(userData.talents) && userData.talents.length > 0) {
                    // Extract talent IDs using db_id field instead of id
                    const extractedTalentIds = userData.talents.map(talent => {
                        // Use db_id field as the talent ID
                        const talentId = talent.db_id;
                        console.log(`Talent: ${talent.name}, using db_id: ${talentId}`);
                        return talentId;
                    });
                    
                    console.log("Talent IDs extracted:", extractedTalentIds);
                    setTalentIds(extractedTalentIds);
                } else {
                    console.warn("No talents array found in user data:", userData);
                    setTalentIds([]);
                }
            } else {
                console.warn("Unexpected response format:", response);
                setError("Failed to load user profile data");
                setTalentIds([]);
            }
        } catch (error) {
            console.error("Error fetching user profile:", error);
            setError("Failed to load user profile: " + (error.message || "Unknown error"));
            setTalentIds([]);
        } finally {
            setIsLoading(false);
        }
    };

    const switchView = (viewType) => {
        setView(viewType);
    };

    console.log("Current state:", { isLoading, talentIds, error });

    // Immediate workaround for debugging - if no talents found after 5 seconds, show error
    useEffect(() => {
        if (isLoading) {
            const timer = setTimeout(() => {
                if (isLoading) {
                    console.warn("Loading timeout reached - showing debug info");
                    setError("Loading timeout exceeded. Debug info: User email: " + userEmail);
                    setIsLoading(false);
                }
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [isLoading, userEmail]);

    return (
        <div className="content-wrapper page">
            <div className='content-wrapper'>
                <div className='h1-container' style={{ justifyContent: 'space-between', display: 'flex' }}>
                    <h1>Projects</h1>
                    <button 
                        className={view === 'list' ? 'big-button-active' : 'big-button'} 
                        onClick={() => navigate('/talent/orders')}
                    >
                        List
                    </button>
                </div>
                
                {isLoading ? (
                    <div>
                        <p>Loading your bookings...</p>
                        <p><small>Debug: Looking for talents associated with user email {userEmail}</small></p>
                    </div>
                ) : error ? (
                    <div style={{ color: 'red' }}>
                        <p>{error}</p>
                        <button 
                            className="button" 
                            onClick={() => window.location.reload()}
                        >
                            Retry
                        </button>
                    </div>
                ) : talentIds.length === 0 ? (
                    <div>
                        <p>No talents found associated with your account.</p>
                        <p><small>If you believe this is an error, please contact support.</small></p>
                    </div>
                ) : (
                    <div className="calendar-container">
                        {/* Log what we're trying to render */}
                        {console.log("Rendering calendar with primary talent:", talentIds[0], "and related talents:", talentIds.slice(1))}
                        <TalentCalendarProvider talentId={talentIds[0]}>
                            <TalentCalendarApi 
                                talentId={talentIds[0]} 
                                relatedTalents={talentIds.slice(1)} // Pass the rest of the talentIds as related
                                showAvailabilities={true}
                            />
                        </TalentCalendarProvider>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MyBookingsPage;
